(() => {
  /**
   * Dev-only logging. Active only on a development/preview theme
   * (`shopify theme dev`), and also on unpublished previews, never on the
   * live (`main`) theme. Toggle scope by editing the role check below.
   */
  const M_EASIFY_DEBUG = !!(
    window.Shopify &&
    window.Shopify.theme &&
    window.Shopify.theme.role &&
    window.Shopify.theme.role !== 'main'
  );
  const mLog = (...args) => {
    if (M_EASIFY_DEBUG) console.log('[m-easify]', ...args);
  };

  function parseMoneyToMinor(text) {
    if (!text) return null;
    try {
      const numeric = String(text)
        .replace(/[^0-9.,]/g, '')
        .replace(/,/g, '');
      const pounds = parseFloat(numeric);
      if (Number.isFinite(pounds)) return Math.round(pounds * 100);
    } catch {}
    return null;
  }

  function getCurrentPriceMinor(product) {
    if (!product) return null;
    let priceEl = product.querySelector(
      '.product-info__price .price .price__current.tpo_product_price'
    );
    if (!priceEl)
      priceEl = product.querySelector('.product-info__price .price .price__current .js-value');
    if (!priceEl) priceEl = product.querySelector('.product-info__price .price .price__current');
    if (!priceEl) return null;
    return parseMoneyToMinor(priceEl.textContent || '');
  }

  function getCurrentPriceText(product) {
    if (!product) return '';
    let priceEl = product.querySelector(
      '.product-info__price .price .price__current.tpo_product_price'
    );
    if (!priceEl)
      priceEl = product.querySelector('.product-info__price .price .price__current .js-value');
    if (!priceEl) priceEl = product.querySelector('.product-info__price .price .price__current');
    return priceEl && priceEl.textContent ? priceEl.textContent.trim() : '';
  }

  function refreshKlarna(product, amountMinor) {
    if (!product) return;
    product.querySelectorAll('klarna-placement').forEach((el) => {
      el.setAttribute('data-purchase-amount', String(amountMinor));
    });
    try {
      if (
        window.Klarna &&
        window.Klarna.OnSiteMessaging &&
        typeof window.Klarna.OnSiteMessaging.refresh === 'function'
      ) {
        window.Klarna.OnSiteMessaging.refresh();
      }
    } catch {}
  }

  function refreshSquare(product, amountMinor) {
    if (!product) return;
    const amountMajor = Math.round(Number(amountMinor || 0) / 100);
    product.querySelectorAll('square-placement').forEach((el) => {
      el.setAttribute('data-amount', String(amountMajor));
      try {
        el.dispatchEvent(new CustomEvent('on:os-message:update', { bubbles: true }));
      } catch {}
    });
    // Best-effort globals (no-ops if absent)
    try {
      if (window.AfterPay && typeof window.AfterPay.refresh === 'function')
        window.AfterPay.refresh();
      if (window.Afterpay && typeof window.Afterpay.refresh === 'function')
        window.Afterpay.refresh();
    } catch {}
  }

  function dispatchUnifiedPriceEvent(product, amountMinor) {
    try {
      product.dispatchEvent(
        new CustomEvent('on:product-price:change', {
          bubbles: true,
          detail: { amountMinor, amount: amountMinor },
        })
      );
    } catch {}
  }

  function refreshShopPay(product, _amountMinor) {
    if (!product) return;
    const priceText = getCurrentPriceText(product);
    product.querySelectorAll('.js-instalments-form shopify-payment-terms').forEach((el) => {
      try {
        const metaStr = el.getAttribute('shopify-meta');
        if (!metaStr) return;
        const meta = JSON.parse(metaStr);
        const form = el.closest('form');
        const variantInput = form ? form.querySelector('input[name="id"]') : null;
        const variantId = variantInput ? variantInput.value : null;

        if (Array.isArray(meta.variants) && meta.variants.length > 0) {
          // Update full_price string to the current displayed price string
          meta.variants[0].full_price = priceText || meta.variants[0].full_price;
        }

        if (variantId) {
          el.setAttribute('variant-id', String(variantId));
        }

        // Setting the attribute triggers the widget to refresh itself
        el.setAttribute('shopify-meta', JSON.stringify(meta));
      } catch {
        // no-op
      }
    });
  }

  function refreshPayPal(product, amountMinor) {
    if (!product) return;
    const amountMajorDecimal = (Number(amountMinor || 0) / 100).toFixed(2);
    product.querySelectorAll('[data-pp-message]').forEach((el) => {
      el.setAttribute('data-pp-amount', String(amountMajorDecimal));
      try {
        if (window.paypal && typeof window.paypal.Messages === 'function') {
          window.paypal.Messages({ amount: amountMajorDecimal }).render(el);
        } else {
          el.dispatchEvent(new CustomEvent('on:pp-message:update', { bubbles: true }));
        }
      } catch {}
    });
  }

  function debounce(fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function initForProduct(product) {
    const runUpdate = () => {
      const amountMinor = getCurrentPriceMinor(product);
      if (typeof amountMinor === 'number') {
        refreshKlarna(product, amountMinor);
        refreshSquare(product, amountMinor);
        refreshPayPal(product, amountMinor);
        refreshShopPay(product, amountMinor);
        dispatchUnifiedPriceEvent(product, amountMinor);
      }
    };

    const debouncedUpdate = debounce(runUpdate, 150);

    // Initial sync
    runUpdate();

    // Convert bullet-separated descriptions into real lists (idempotent).
    initBulletLists(product);

    // Watch Easify/UI price changes. Enterprise 2.3.0 replaces the price row on
    // every variant change, so observe the stable price block around it.
    const priceEl = product.querySelector('.product-info__price');
    const priceRoot = priceEl ? priceEl.closest('.product-price') || priceEl : null;
    if (priceRoot && 'MutationObserver' in window) {
      const mo = new MutationObserver(() => debouncedUpdate());
      mo.observe(priceRoot, { childList: true, characterData: true, subtree: true });
    }

    // The variant picker is replaced on every variant change too; the event
    // bubbles, so listen on the product.
    product.addEventListener('on:variant:change', debouncedUpdate);
  }

  /**
   * Forward clicks anywhere on a collapsible group card to its label-row
   * toggle, so the entire `.tpo_option-group_wrapper` acts as the hit
   * target — not just the title strip Easify wires up by default.
   *
   * Clicks are ignored when they land on an actual interactive descendant
   * (an option card, popup-modal button, link, tooltip, form control, …)
   * so the existing behaviour of those controls is preserved.
   */
  const TITLE_ICON_PLACEHOLDER_REGEX =
    /\{\{\s*(storage|ram|wifi|network|gpu|warranty|os|bundle|monitor|power_supply|keyboards|mice|audio)_icon\s*\}\}/gi;
  const TITLE_ICON_PLACEHOLDER_TEST_REGEX =
    /\{\{\s*(storage|ram|wifi|network|gpu|warranty|os|bundle|monitor|power_supply|keyboards|mice|audio)_icon\s*\}\}/i;
  const TITLE_ICON_ALIASES = {
    network: 'wifi',
  };

  // Warranty badge token — a pill (shield icon + inline text) for group
  // titles, e.g. `Monitors {{ warranty_badge:3 years warranty }}`. Distinct
  // from the `{{ warranty_icon }}` token above (icon only). The text is
  // optional and may use `~` for spaces.
  const WARRANTY_BADGE_REGEX = /\{\{\s*warranty_badge(?::([^}]*))?\s*\}\}/gi;
  const WARRANTY_BADGE_TEST_REGEX = /\{\{\s*warranty_badge(?::[^}]*)?\s*\}\}/i;

  const TITLE_ICONS = {
    storage: `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="12" viewBox="0 0 21 12" fill="none">
<path d="M19.5 0H1.5C1.10218 0 0.720644 0.158036 0.43934 0.43934C0.158035 0.720645 0 1.10218 0 1.5V10.5C0 10.8978 0.158035 11.2794 0.43934 11.5607C0.720644 11.842 1.10218 12 1.5 12H19.5C19.8978 12 20.2794 11.842 20.5607 11.5607C20.842 11.2794 21 10.8978 21 10.5V1.5C21 1.10218 20.842 0.720645 20.5607 0.43934C20.2794 0.158036 19.8978 0 19.5 0ZM19.5 10.5H1.5V1.5H19.5V10.5ZM17.25 6C17.25 6.2225 17.184 6.44001 17.0604 6.62502C16.9368 6.81002 16.7611 6.95422 16.5555 7.03936C16.35 7.12451 16.1238 7.14679 15.9055 7.10338C15.6873 7.05998 15.4868 6.95283 15.3295 6.79549C15.1722 6.63816 15.065 6.4377 15.0216 6.21948C14.9782 6.00125 15.0005 5.77505 15.0856 5.56948C15.1708 5.36391 15.315 5.18821 15.5 5.0646C15.685 4.94098 15.9025 4.875 16.125 4.875C16.4234 4.875 16.7095 4.99353 16.9205 5.2045C17.1315 5.41548 17.25 5.70163 17.25 6Z" fill="currentColor"/></svg>`,
    ram: `<svg xmlns="http://www.w3.org/2000/svg" width="23" height="15" viewBox="0 0 23 15" fill="none">
<path d="M21 0H1.5C1.10218 0 0.720644 0.158035 0.43934 0.43934C0.158035 0.720644 0 1.10218 0 1.5V13.5C0 13.6989 0.0790176 13.8897 0.21967 14.0303C0.360322 14.171 0.551088 14.25 0.75 14.25C0.948912 14.25 1.13968 14.171 1.28033 14.0303C1.42098 13.8897 1.5 13.6989 1.5 13.5V12H3V13.5C3 13.6989 3.07902 13.8897 3.21967 14.0303C3.36032 14.171 3.55109 14.25 3.75 14.25C3.94891 14.25 4.13968 14.171 4.28033 14.0303C4.42098 13.8897 4.5 13.6989 4.5 13.5V12H6V13.5C6 13.6989 6.07902 13.8897 6.21967 14.0303C6.36032 14.171 6.55109 14.25 6.75 14.25C6.94891 14.25 7.13968 14.171 7.28033 14.0303C7.42098 13.8897 7.5 13.6989 7.5 13.5V12H9V13.5C9 13.6989 9.07902 13.8897 9.21967 14.0303C9.36032 14.171 9.55109 14.25 9.75 14.25C9.94891 14.25 10.1397 14.171 10.2803 14.0303C10.421 13.8897 10.5 13.6989 10.5 13.5V12H12V13.5C12 13.6989 12.079 13.8897 12.2197 14.0303C12.3603 14.171 12.5511 14.25 12.75 14.25C12.9489 14.25 13.1397 14.171 13.2803 14.0303C13.421 13.8897 13.5 13.6989 13.5 13.5V12H15V13.5C15 13.6989 15.079 13.8897 15.2197 14.0303C15.3603 14.171 15.5511 14.25 15.75 14.25C15.9489 14.25 16.1397 14.171 16.2803 14.0303C16.421 13.8897 16.5 13.6989 16.5 13.5V12H18V13.5C18 13.6989 18.079 13.8897 18.2197 14.0303C18.3603 14.171 18.5511 14.25 18.75 14.25C18.9489 14.25 19.1397 14.171 19.2803 14.0303C19.421 13.8897 19.5 13.6989 19.5 13.5V12H21V13.5C21 13.6989 21.079 13.8897 21.2197 14.0303C21.3603 14.171 21.5511 14.25 21.75 14.25C21.9489 14.25 22.1397 14.171 22.2803 14.0303C22.421 13.8897 22.5 13.6989 22.5 13.5V1.5C22.5 1.10218 22.342 0.720644 22.0607 0.43934C21.7794 0.158035 21.3978 0 21 0ZM1.5 1.5H21V10.5H1.5V1.5ZM9.75 9C9.94891 9 10.1397 8.92098 10.2803 8.78033C10.421 8.63968 10.5 8.44891 10.5 8.25V3.75C10.5 3.55109 10.421 3.36032 10.2803 3.21967C10.1397 3.07902 9.94891 3 9.75 3H3.75C3.55109 3 3.36032 3.07902 3.21967 3.21967C3.07902 3.36032 3 3.55109 3 3.75V8.25C3 8.44891 3.07902 8.63968 3.21967 8.78033C3.36032 8.92098 3.55109 9 3.75 9H9.75ZM4.5 4.5H9V7.5H4.5V4.5ZM12.75 9H18.75C18.9489 9 19.1397 8.92098 19.2803 8.78033C19.421 8.63968 19.5 8.44891 19.5 8.25V3.75C19.5 3.55109 19.421 3.36032 19.2803 3.21967C19.1397 3.07902 18.9489 3 18.75 3H12.75C12.5511 3 12.3603 3.07902 12.2197 3.21967C12.079 3.36032 12 3.55109 12 3.75V8.25C12 8.44891 12.079 8.63968 12.2197 8.78033C12.3603 8.92098 12.5511 9 12.75 9ZM13.5 4.5H18V7.5H13.5V4.5Z" fill="currentColor"/>
</svg>`,
    wifi: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none">
<path d="M13 5H2C1.30964 5 0.75 5.55964 0.75 6.25V11.75C0.75 12.4404 1.30964 13 2 13H13C13.6904 13 14.25 12.4404 14.25 11.75V6.25C14.25 5.55964 13.6904 5 13 5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6.75 7H3.75C3.47386 7 3.25 7.22386 3.25 7.5V9.5C3.25 9.77614 3.47386 10 3.75 10H6.75C7.02614 10 7.25 9.77614 7.25 9.5V7.5C7.25 7.22386 7.02614 7 6.75 7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.75 13V14.5M4.75 13V14.5M6.75 13V14.5M8.75 13V14.5M10.75 13V14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M14.25 6.5H16C16.7 6.5 17.25 7.05 17.25 7.75V10.25C17.25 10.95 16.7 11.5 16 11.5H14.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.25 3.75C13.65 3.75 14.75 4.85 14.75 6.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.75 2.25C14.95 2.25 16.75 4.05 16.75 6.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.75 0.75C14.5 0.75 16.75 3 16.75 5.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    gpu: '<svg viewBox="0 0 32 32" fill="none" focusable="false" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M.799 2.983a.8.8 0 1 0 0 1.599h2.4V26.42h-2.4a.8.8 0 1 0 0 1.599h2.4c.875 0 1.601-.726 1.601-1.6v-2.3h2.4v2.3c0 .874.726 1.6 1.6 1.6H28c.874 0 1.6-.726 1.6-1.6v-2.3h.8c.874 0 1.6-.726 1.6-1.6V10.958c0-.724-.289-1.419-.8-1.932L28.693 6.52a2.73 2.73 0 0 0-1.932-.799H4.799v-1.14c0-.874-.726-1.6-1.601-1.6H.799ZM4.8 7.322h21.962c.3 0 .588.119.8.332l2.506 2.506c.213.213.332.5.332.8v11.56H4.798L4.8 7.322Zm17.312 1.2a6.4 6.4 0 1 0 0 12.801 6.4 6.4 0 0 0 0-12.8Zm0 5.199a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4ZM7.486 11.798a.8.8 0 1 0 0 1.599h5.807a.8.8 0 1 0 0-1.599H7.486Zm0 4.646a.8.8 0 1 0 0 1.599h5.806a.8.8 0 1 0 0-1.599H7.486ZM8.798 24.12h19.199v2.3H8.798v-2.3Z" fill="currentColor"/></svg>',
    warranty: `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="23" viewBox="0 0 19 23" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M0 14.7195C0 11.6602 0.00497805 7.65552 0.0074613 5.80025C0.00911295 4.8581 0.542746 3.99716 1.38711 3.57788L8.0633 0.260081C8.76345 -0.0887634 9.588 -0.0862674 10.2865 0.265071L16.869 3.57707C17.7085 3.99884 18.2379 4.85811 18.2379 5.79776V14.7195C18.2379 16.5914 16.8086 18.3737 14.9988 19.7774C12.523 21.6973 9.42884 22.9452 9.42884 22.9452C9.22995 23.0256 9.00796 23.0256 8.80907 22.9452C8.80907 22.9452 5.71499 21.6973 3.23907 19.7774C1.42937 18.3737 0 16.5914 0 14.7195ZM1.65724 14.7195C1.65724 16.1406 2.88028 17.4026 4.25496 18.4682C6.09947 19.8984 8.31437 20.9267 9.11976 21.2772C9.92272 20.9267 12.1385 19.8984 13.983 18.4682C15.3577 17.4026 16.5807 16.1406 16.5807 14.7195V5.79776C16.5807 5.48455 16.4042 5.19783 16.1241 5.05697L9.54157 1.7458C9.30872 1.62813 9.03441 1.62814 8.80075 1.74415L2.12459 5.06195C1.84368 5.20198 1.66553 5.4887 1.6647 5.80274C1.66222 7.6572 1.65724 11.6611 1.65724 14.7195ZM4.84163 11.9884C4.49692 11.6876 4.46046 11.1639 4.76126 10.8192C5.06204 10.4745 5.58573 10.438 5.93043 10.7388L8.22157 12.735L12.233 8.35903C12.542 8.02179 13.0665 7.9994 13.4038 8.30848C13.741 8.61756 13.7633 9.14207 13.4542 9.47931L8.89685 14.4511C8.59366 14.7824 8.0807 14.8106 7.7418 14.5156L4.84163 11.9884Z" fill="currentColor"/>
</svg>`,
    os: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
<path d="M17.25 9.74993H9.75C9.55109 9.74993 9.36032 9.82895 9.21967 9.9696C9.07902 10.1103 9 10.301 9 10.4999V15.8859C8.99981 16.0617 9.06137 16.232 9.17395 16.367C9.28653 16.502 9.44297 16.5932 9.61594 16.6246L17.1159 17.9877C17.1601 17.996 17.205 18.0001 17.25 17.9999C17.4489 17.9999 17.6397 17.9209 17.7803 17.7803C17.921 17.6396 18 17.4488 18 17.2499V10.4999C18 10.301 17.921 10.1103 17.7803 9.9696C17.6397 9.82895 17.4489 9.74993 17.25 9.74993ZM16.5 16.3509L10.5 15.2606V11.2499H16.5V16.3509ZM6.75 9.74993H0.75C0.551088 9.74993 0.360322 9.82895 0.21967 9.9696C0.0790176 10.1103 0 10.301 0 10.4999V14.2499C2.53849e-05 14.4256 0.0616938 14.5956 0.174254 14.7305C0.286814 14.8653 0.443126 14.9564 0.615937 14.9877L6.61594 16.079C6.6602 16.0868 6.70505 16.0909 6.75 16.0912C6.94891 16.0912 7.13968 16.0122 7.28033 15.8715C7.42098 15.7309 7.5 15.5401 7.5 15.3412V10.4999C7.5 10.301 7.42098 10.1103 7.28033 9.9696C7.13968 9.82895 6.94891 9.74993 6.75 9.74993ZM6 14.4421L1.5 13.6237V11.2499H6V14.4421ZM17.7309 0.174309C17.6466 0.103863 17.5478 0.0528212 17.4415 0.0247943C17.3352 -0.00323248 17.2241 -0.00755885 17.1159 0.0121211L9.61594 1.37618C9.44313 1.40758 9.28681 1.49863 9.17425 1.63346C9.06169 1.76829 9.00003 1.93836 9 2.114V7.49993C9 7.69885 9.07902 7.88961 9.21967 8.03026C9.36032 8.17092 9.55109 8.24993 9.75 8.24993H17.25C17.4489 8.24993 17.6397 8.17092 17.7803 8.03026C17.921 7.88961 18 7.69885 18 7.49993V0.749933C18 0.640141 17.9759 0.531683 17.9294 0.432219C17.8829 0.332756 17.8152 0.244713 17.7309 0.174309ZM16.5 6.74993H10.5V2.73931L16.5 1.649V6.74993ZM7.23094 2.08306C7.14657 2.01261 7.04776 1.96157 6.94148 1.93354C6.83521 1.90552 6.72407 1.90119 6.61594 1.92087L0.615937 3.01212C0.443126 3.04352 0.286814 3.13457 0.174254 3.2694C0.0616938 3.40423 2.53849e-05 3.57429 0 3.74993V7.49993C0 7.69885 0.0790176 7.88961 0.21967 8.03026C0.360322 8.17092 0.551088 8.24993 0.75 8.24993H6.75C6.94891 8.24993 7.13968 8.17092 7.28033 8.03026C7.42098 7.88961 7.5 7.69885 7.5 7.49993V2.65868C7.50002 2.54889 7.47593 2.44043 7.42944 2.34097C7.38295 2.24151 7.31518 2.15346 7.23094 2.08306ZM6 6.74993H1.5V4.37618L6 3.56243V6.74993Z" fill="currentColor"/>
</svg>`,
    bundle: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="11" viewBox="0 0 22 11" fill="none">
<path d="M11.4286 2.38098H1.90477C1.24729 2.38098 0.714294 2.91398 0.714294 3.57146V7.14289C0.714294 7.80037 1.24729 8.33336 1.90477 8.33336H11.4286C12.0861 8.33336 12.6191 7.80037 12.6191 7.14289V3.57146C12.6191 2.91398 12.0861 2.38098 11.4286 2.38098Z" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.61908 4.28577H3.57146M4.52384 4.28577H5.47622M6.4286 4.28577H7.38098M8.33336 4.28577H9.28575M10.2381 4.28577H10.9524" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.61908 6.19049H9.28575M10.2381 6.19049H10.9524" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.8571 0.714294C19.4286 0.714294 20.7143 2.00001 20.7143 3.57144V6.90477C20.7143 8.80953 19.4286 10 17.8571 10C16.2857 10 15 8.80953 15 6.90477V3.57144C15 2.00001 16.2857 0.714294 17.8571 0.714294Z" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.8572 0.714294V4.04763" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    monitor: `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="16" viewBox="0 0 19 16" fill="none">
<path d="M16.5 0.75H2C1.30964 0.75 0.75 1.30964 0.75 2V10.5C0.75 11.1904 1.30964 11.75 2 11.75H16.5C17.1904 11.75 17.75 11.1904 17.75 10.5V2C17.75 1.30964 17.1904 0.75 16.5 0.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7.25 14.75H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.25 11.75V14.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M5.25 14.75H13.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    power_supply: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
<rect x="3" y="6" width="17" height="12" rx="2" stroke="currentColor" stroke-width="1.75"/>
<circle cx="9" cy="12" r="3.25" stroke="currentColor" stroke-width="1.75"/>
<path d="M9 8.75V15.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
<path d="M5.75 12H12.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
<path d="M15.25 9.5H17.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
<path d="M15.25 12H17.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
<path d="M15.25 14.5H17.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
<path d="M20 10H21C21.55 10 22 10.45 22 11V13C22 13.55 21.55 14 21 14H20" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    keyboards: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M14.5 7H9.5C6.21252 7 4.56878 7 3.46243 7.90796C3.25989 8.07418 3.07418 8.25989 2.90796 8.46243C2 9.56878 2 11.2125 2 14.5C2 17.7875 2 19.4312 2.90796 20.5376C3.07418 20.7401 3.25989 20.9258 3.46243 21.092C4.56878 22 6.21252 22 9.5 22H14.5C17.7875 22 19.4312 22 20.5376 21.092C20.7401 20.9258 20.9258 20.7401 21.092 20.5376C22 19.4312 22 17.7875 22 14.5C22 11.2125 22 9.56878 21.092 8.46243C20.9258 8.25989 20.7401 8.07418 20.5376 7.90796C19.4312 7 17.7875 7 14.5 7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
<path d="M12 7V5C12 4.44772 12.4477 4 13 4C13.5523 4 14 3.55228 14 3V2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7 12H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.5 12H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M16 12H17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7 17H17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    mice: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M12 2V6M12 10V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M4.74061 17.0888C4.93047 19.4803 6.82417 21.511 9.26502 21.8118C10.1626 21.9223 11.0752 22 12 22C12.9247 22 13.8373 21.9223 14.7349 21.8118C17.1758 21.511 19.0694 19.4803 19.2593 17.0888C19.3909 15.4317 19.5 13.732 19.5 12C19.5 10.268 19.3909 8.56832 19.2593 6.91118C19.0694 4.51965 17.1758 2.48893 14.7349 2.1882C13.8373 2.07762 12.9247 2 12 2C11.0752 2 10.1626 2.07762 9.26502 2.1882C6.82417 2.48893 4.93047 4.51965 4.74061 6.91118C4.60903 8.56832 4.5 10.268 4.5 12C4.5 13.732 4.60903 15.4317 4.74061 17.0888Z" stroke="currentColor" stroke-width="1.5"/>
<path d="M10.5 7.5C10.5 7.03406 10.5 6.80109 10.5761 6.61732C10.6776 6.37229 10.8723 6.17761 11.1173 6.07612C11.3011 6 11.5341 6 12 6C12.4659 6 12.6989 6 12.8827 6.07612C13.1277 6.17761 13.3224 6.37229 13.4239 6.61732C13.5 6.80109 13.5 7.03406 13.5 7.5V8.5C13.5 8.96594 13.5 9.19891 13.4239 9.38268C13.3224 9.62771 13.1277 9.82239 12.8827 9.92388C12.6989 10 12.4659 10 12 10C11.5341 10 11.3011 10 11.1173 9.92388C10.8723 9.82239 10.6776 9.62771 10.5761 9.38268C10.5 9.19891 10.5 8.96594 10.5 8.5V7.5Z" stroke="currentColor" stroke-width="1.5"/>
</svg>`,
    audio: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M15 12C15 10.8954 15.8954 10 17 10C19.2091 10 21 11.7909 21 14C21 16.2091 19.2091 18 17 18C15.8954 18 15 17.1046 15 16V12Z" stroke="currentColor" stroke-width="1.5"/>
<path d="M9 12C9 10.8954 8.10457 10 7 10C4.79086 10 3 11.7909 3 14C3 16.2091 4.79086 18 7 18C8.10457 18 9 17.1046 9 16V12Z" stroke="currentColor" stroke-width="1.5"/>
<path d="M3 14V11C3 6.02944 7.02944 2 12 2C16.9706 2 21 6.02944 21 11V15.8462C21 17.8545 21 18.8586 20.6476 19.6417C20.2465 20.5329 19.5329 21.2465 18.6417 21.6476C17.8586 22 16.8545 22 14.8462 22H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  };

  function createTitleIcon(name) {
    const key = TITLE_ICON_ALIASES[name] || name;
    const svg = TITLE_ICONS[key];
    if (!svg) return document.createTextNode(`{{ ${name}_icon }}`);

    const icon = document.createElement('span');
    icon.className = `m-easify-title-icon m-easify-title-icon--${key}`;
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = svg;
    return icon;
  }

  function replaceTitleIconPlaceholder(textNode) {
    const text = textNode.nodeValue || '';
    if (!TITLE_ICON_PLACEHOLDER_TEST_REGEX.test(text)) return;

    TITLE_ICON_PLACEHOLDER_REGEX.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let match;

    while ((match = TITLE_ICON_PLACEHOLDER_REGEX.exec(text))) {
      if (match.index > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)));
      }
      fragment.appendChild(createTitleIcon(match[1].toLowerCase()));
      cursor = match.index + match[0].length;
      if (text.charAt(cursor) === ' ') cursor += 1;
    }

    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  }

  function removeTitleIconPlaceholders(text) {
    TITLE_ICON_PLACEHOLDER_REGEX.lastIndex = 0;
    return String(text || '')
      .replace(TITLE_ICON_PLACEHOLDER_REGEX, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function replaceTitleIconAttributesWithin(scope) {
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[aria-label], [title], [placeholder], [alt]').forEach((el) => {
      ['aria-label', 'title', 'placeholder', 'alt'].forEach((attr) => {
        const value = el.getAttribute(attr);
        if (value && TITLE_ICON_PLACEHOLDER_TEST_REGEX.test(value)) {
          el.setAttribute(attr, removeTitleIconPlaceholders(value));
        }
      });
    });
  }

  /** Build the warranty pill (shield icon + optional text). */
  function createWarrantyBadge(rawText) {
    const text = String(rawText || '')
      .replace(/~/g, ' ')
      .trim();
    const badge = document.createElement('span');
    badge.className = 'm-easify-warranty-badge';

    const icon = document.createElement('span');
    icon.className = 'm-easify-warranty-badge__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = TITLE_ICONS.warranty;
    badge.appendChild(icon);

    if (text) {
      const label = document.createElement('span');
      label.className = 'm-easify-warranty-badge__text';
      label.textContent = text;
      badge.appendChild(label);
    }
    return badge;
  }

  /** Strip the warranty token out of a plain-text attribute value. */
  function stripWarrantyBadgeTokens(value) {
    WARRANTY_BADGE_REGEX.lastIndex = 0;
    return String(value || '')
      .replace(WARRANTY_BADGE_REGEX, (_m, t) => (t ? t.replace(/~/g, ' ').trim() : ''))
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function replaceWarrantyBadgePlaceholder(textNode) {
    const text = textNode.nodeValue || '';
    if (!WARRANTY_BADGE_TEST_REGEX.test(text)) return;

    WARRANTY_BADGE_REGEX.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let match;

    while ((match = WARRANTY_BADGE_REGEX.exec(text))) {
      if (match.index > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)));
      }
      fragment.appendChild(createWarrantyBadge(match[1]));
      cursor = match.index + match[0].length;
      if (text.charAt(cursor) === ' ') cursor += 1;
    }

    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  }

  function replaceWarrantyBadgesWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;

    // Keep the raw token out of accessible names (aria-label/title/etc.).
    scope.querySelectorAll('[aria-label], [title], [placeholder], [alt]').forEach((el) => {
      ['aria-label', 'title', 'placeholder', 'alt'].forEach((attr) => {
        const value = el.getAttribute(attr);
        if (value && WARRANTY_BADGE_TEST_REGEX.test(value)) {
          el.setAttribute(attr, stripWarrantyBadgeTokens(value));
        }
      });
    });

    if (!document.createTreeWalker) return;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!WARRANTY_BADGE_TEST_REGEX.test(node.nodeValue || '')) {
          return NodeFilter.FILTER_REJECT;
        }
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, svg')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(replaceWarrantyBadgePlaceholder);
  }

  function replaceTitleIconsWithin(root) {
    if (!root || !document.createTreeWalker) return;
    const scope = root.nodeType === 1 ? root : document;
    replaceTitleIconAttributesWithin(scope);
    replaceWarrantyBadgesWithin(scope);

    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!TITLE_ICON_PLACEHOLDER_TEST_REGEX.test(node.nodeValue || '')) {
          return NodeFilter.FILTER_REJECT;
        }
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, svg')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(replaceTitleIconPlaceholder);
  }

  function initTitleIcons(product) {
    if (!product || product.__mEasifyTitleIconsBound) return;
    product.__mEasifyTitleIconsBound = true;

    replaceTitleIconsWithin(product);

    if (!('MutationObserver' in window)) return;

    let scheduled = false;
    const scheduleScan = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        replaceTitleIconsWithin(product);
      });
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          const value = mutation.target.nodeValue || '';
          if (
            TITLE_ICON_PLACEHOLDER_TEST_REGEX.test(value) ||
            WARRANTY_BADGE_TEST_REGEX.test(value)
          ) {
            scheduleScan();
            return;
          }
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 || node.nodeType === 3) {
            scheduleScan();
            return;
          }
        }
      }
    });

    observer.observe(product, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Pure CSS can't insert line breaks at specific characters in a text node,
   * so we transform the bullet-separated description / selected-value spans
   * into a real `<ul>` of items. Each processed element is flagged with
   * `data-bulletized` so we never re-process the same node, and a
   * MutationObserver keeps it working when Easify re-renders descriptions
   * after a selection change.
   */
  const BULLET_TARGET_SELECTOR = [
    '.tpo_option_type_description',
    '.tpo_selected_values_span',
    '.tpo_option-help-text',
  ].join(',');

  // Normalises the unicode/whitespace variants of "•" Easify (and copy-pasted
  // theme content) tend to ship with.
  const BULLET_SPLIT_REGEX = /\s*[•·∙]\s*/g;

  function bulletizeElement(el) {
    if (!el || el.dataset.bulletized === '1') return;

    // Warranty copy in the "+ / **" syntax belongs to formatWarrantyText().
    // Bail out (without marking) so the two can never fight, whichever
    // observer happens to run first.
    if (isWarrantyOwned(el)) return;

    // If we've already injected our list (e.g. previous run), skip.
    if (el.querySelector(':scope > .m-easify-bullet-list')) {
      el.dataset.bulletized = '1';
      return;
    }

    const raw = (el.textContent || '').trim();
    if (!raw || !BULLET_SPLIT_REGEX.test(raw)) {
      // Plain text or <br>-separated content — let the browser render it as
      // is. <br> intentionally does NOT trigger the bulleted list.
      el.dataset.bulletized = '1';
      return;
    }
    BULLET_SPLIT_REGEX.lastIndex = 0;

    const parts = raw
      .split(BULLET_SPLIT_REGEX)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 2) {
      el.dataset.bulletized = '1';
      return;
    }

    const list = document.createElement('ul');
    list.className = 'm-easify-bullet-list';
    parts.forEach((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      list.appendChild(item);
    });

    el.textContent = '';
    el.appendChild(list);
    el.dataset.bulletized = '1';
  }

  function bulletizeWithin(root) {
    if (!root) return;
    const scope = root.nodeType === 1 ? root : document;
    const matches = scope.querySelectorAll ? scope.querySelectorAll(BULLET_TARGET_SELECTOR) : [];
    matches.forEach(bulletizeElement);
    if (scope.matches && scope.matches(BULLET_TARGET_SELECTOR)) {
      bulletizeElement(scope);
    }
  }

  function initBulletLists(product) {
    if (!product || product.__mEasifyBulletsBound) return;
    product.__mEasifyBulletsBound = true;

    // Initial sweep — covers anything Easify has already rendered.
    bulletizeWithin(product);

    if (!('MutationObserver' in window)) return;

    // Easify injects (and re-renders) the option-set wrapper asynchronously,
    // so we observe the whole product subtree. Re-scans are coalesced into one
    // animation frame so we never thrash while Easify is mid-write.
    let scheduled = false;
    const scheduleScan = (node) => {
      const target = node && node.nodeType === 1 ? node : product;
      if (target.dataset && target.dataset.bulletized === '1') {
        delete target.dataset.bulletized;
      }
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        bulletizeWithin(product);
      });
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          // Either the added node itself is one of our targets, or it
          // contains some — let scheduleScan re-walk the product once.
          scheduleScan(node);
        }
        if (mutation.type === 'characterData' && mutation.target.parentElement) {
          const parent = mutation.target.parentElement.closest(BULLET_TARGET_SELECTOR);
          if (parent) scheduleScan(parent);
        }
      }
    });

    observer.observe(product, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Warranty Cover widget (EM-246).
   *
   * Tags the "Warranty Upgrade" radio group so m-easify.css can restyle it,
   * and turns its plain-text Easify copy into structured lines. Easify stores
   * each description as ONE line, so "+ " is an inline token, not a line start:
   *   "Lead text + Benefit one + Benefit **two**"
   *   - text before the first " + " token -> `p.m-easify-wty-note`
   *   - each segment after a " + " token  -> `.m-easify-wty-add` benefit row
   *   - `**text**`                        -> <strong>text</strong>
   * Real line breaks (\n, <br>, blocks) also split segments, in case Easify
   * ever keeps them. Copy is only touched when it starts with "+ ", contains
   * " + " or contains "**", and never when it contains a "•"-style bullet —
   * the old copy (e.g. "… • +1 year free collection • …") stays with
   * bulletizeElement().
   *
   * Each tier wrapper also gets `m-easify-wty-tier--{metal}` (from the input
   * value's first word) and a `.m-easify-wty-price` span that shows the price
   * without Easify's brackets, or "Included" for a free tier. Every step is
   * idempotent and writes only when something differs, so the observer
   * settles after one extra (no-op) pass.
   */
  const WARRANTY_INPUT_SELECTOR = 'input.tpo_radio-button[name$="Warranty Upgrade"]';
  const WARRANTY_GROUP_CLASS = 'm-easify-warranty';
  const WTY_TIER_CLASS_PREFIX = 'm-easify-wty-tier--';
  const WTY_PRICE_CLASS = 'm-easify-wty-price';
  const WTY_PRICE_INCLUDED_CLASS = 'm-easify-wty-price--included';
  const WTY_INCLUDED_LABEL = 'Included';
  const WTY_INCLUDED_REGEX = /\bincluded\b/i;
  const WTY_LINE_CLASSES = ['m-easify-wty-add', 'm-easify-wty-note'];
  const WTY_BLOCK_TAG_REGEX = /^(?:P|DIV|LI|UL|OL|H[1-6]|BLOCKQUOTE)$/;
  // Same bullet set as BULLET_SPLIT_REGEX — such copy belongs to the bulletizer.
  const WTY_BULLET_CHAR_REGEX = /[•·∙]/;
  // "+ " at the start or " + " inline. "+1 year" (no space) never matches.
  const WTY_ADD_TOKEN_TEST_REGEX = /(?:^|\s)\+\s/;
  const WTY_ADD_TOKEN_SPLIT_REGEX = /(?:^|\s)\+\s+/;

  /** The warranty `.tpo_option-container` that owns `el`, or null. */
  function getWarrantyGroup(el) {
    const container = el && el.closest ? el.closest('.tpo_option-container') : null;
    return container && container.querySelector(WARRANTY_INPUT_SELECTOR) ? container : null;
  }

  /**
   * Read an element's copy as text. Easify may store line breaks as "\n" in
   * a text node or as <br>/block markup — both become "\n". Lines we
   * generated earlier are ignored (they no longer carry the syntax).
   */
  function readWarrantyText(el) {
    const clone = el.cloneNode(true);
    clone
      .querySelectorAll(WTY_LINE_CLASSES.map((cls) => `.${cls}`).join(','))
      .forEach((node) => node.remove());
    clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    clone.querySelectorAll('*').forEach((node) => {
      if (WTY_BLOCK_TAG_REGEX.test(node.tagName)) {
        node.prepend('\n');
        node.append('\n');
      }
    });
    return (clone.textContent || '').replace(/\r\n?/g, '\n').trim();
  }

  function hasWarrantySyntax(text) {
    if (!text || WTY_BULLET_CHAR_REGEX.test(text)) return false;
    return WTY_ADD_TOKEN_TEST_REGEX.test(text) || text.indexOf('**') !== -1;
  }

  /**
   * Split copy into `{ type: 'note' | 'add', text }` segments: per line, the
   * text before the first "+ " token is a note, every segment after one is a
   * benefit ("add") line. Empty segments are dropped.
   */
  function parseWarrantyCopy(text) {
    const segments = [];
    text.split('\n').forEach((line) => {
      line.split(WTY_ADD_TOKEN_SPLIT_REGEX).forEach((part, index) => {
        const value = part.trim();
        if (value) segments.push({ type: index === 0 ? 'note' : 'add', text: value });
      });
    });
    return segments;
  }

  /** Marked AND still holding only our generated lines (Easify hasn't re-rendered it). */
  function isWarrantyFormatted(el) {
    if (el.dataset.mEasifyWty !== '1' || !el.childNodes.length) return false;
    return Array.prototype.every.call(
      el.childNodes,
      (node) => node.nodeType === 1 && WTY_LINE_CLASSES.some((cls) => node.classList.contains(cls))
    );
  }

  /** True when the warranty formatter owns `el` (checked by bulletizeElement). */
  function isWarrantyOwned(el) {
    if (!getWarrantyGroup(el)) return false;
    if (el.closest('[data-m-easify-wty="1"]') || el.querySelector('[data-m-easify-wty="1"]')) {
      return true;
    }
    return hasWarrantySyntax(readWarrantyText(el));
  }

  /** Append `text` to `parent`, turning matched `**pairs**` into <strong>. */
  function appendWarrantyInline(parent, text) {
    const parts = text.split('**');
    const markers = parts.length - 1;
    const paired = markers - (markers % 2);
    parts.forEach((part, index) => {
      if (index > paired) {
        // Unmatched trailing marker stays literal.
        parent.append(`**${part}`);
        return;
      }
      if (!part) return;
      if (index % 2 === 1) {
        const strong = document.createElement('strong');
        strong.textContent = part;
        parent.append(strong);
        return;
      }
      parent.append(part);
    });
  }

  function buildWarrantyLine(segment) {
    if (segment.type === 'add') {
      const row = document.createElement('div');
      row.className = 'm-easify-wty-add';
      const plus = document.createElement('span');
      plus.className = 'm-easify-wty-add__plus';
      plus.setAttribute('aria-hidden', 'true');
      plus.textContent = '+';
      const text = document.createElement('span');
      text.className = 'm-easify-wty-add__text';
      appendWarrantyInline(text, segment.text);
      row.append(plus, text);
      return row;
    }
    const note = document.createElement('p');
    note.className = 'm-easify-wty-note';
    appendWarrantyInline(note, segment.text);
    return note;
  }

  function formatWarrantyText(el) {
    if (!el || isWarrantyFormatted(el)) return;
    // Old "•" copy that the bulletizer already turned into a list.
    if (el.querySelector('.m-easify-bullet-list')) return;

    const text = readWarrantyText(el);
    const segments = hasWarrantySyntax(text) ? parseWarrantyCopy(text) : [];
    if (!segments.length) {
      if (el.dataset.mEasifyWty) delete el.dataset.mEasifyWty;
      return;
    }

    const fragment = document.createDocumentFragment();
    segments.forEach((segment) => fragment.appendChild(buildWarrantyLine(segment)));
    el.textContent = '';
    el.appendChild(fragment);
    el.dataset.mEasifyWty = '1';
  }

  /** Concatenated direct text nodes of `el` (the tier name, not its children). */
  function getOwnText(el) {
    let text = '';
    el.childNodes.forEach((node) => {
      if (node.nodeType === 3) text += node.nodeValue || '';
    });
    return text;
  }

  /**
   * Mirror Easify's "(£59.99)" into `.m-easify-wty-price` as "£59.99" (CSS
   * hides the original via `:has(+ .m-easify-wty-price)`), or "Included" when
   * the tier is free and its name doesn't already say so. Easify's own price
   * node is never rewritten, so its re-renders can't break the mirror — the
   * observer just re-syncs it.
   */
  function syncWarrantyPrice(wrapper) {
    const label = wrapper.querySelector('.tpo_option_type_label');
    if (!label) return;

    const source = label.querySelector('.tpo_additional-price');
    let mirror = label.querySelector(`:scope > .${WTY_PRICE_CLASS}`);
    const priceText = ((source && source.textContent) || '')
      .trim()
      .replace(/^\(\s*/, '')
      .replace(/\s*\)$/, '')
      .trim();

    let text = priceText;
    const included = !priceText;
    if (included) {
      // Old copy ("Bronze — 3 Years - Included") already says it.
      if (WTY_INCLUDED_REGEX.test(getOwnText(label))) {
        if (mirror) mirror.remove();
        return;
      }
      text = WTY_INCLUDED_LABEL;
    }

    if (!mirror) {
      mirror = document.createElement('span');
      mirror.className = WTY_PRICE_CLASS;
    }
    mirror.classList.toggle(WTY_PRICE_INCLUDED_CLASS, included);
    if (mirror.textContent !== text) mirror.textContent = text;

    if (source && source.parentNode === label) {
      if (source.nextElementSibling !== mirror) source.after(mirror);
    } else if (mirror.parentNode !== label) {
      label.appendChild(mirror);
    }
  }

  function decorateWarrantyTier(wrapper) {
    const input = wrapper.querySelector('input.tpo_radio-button');
    const metal = ((input && input.value) || '')
      .trim()
      .split(/[^A-Za-z]+/)[0]
      .toLowerCase();
    const tierClass = metal ? WTY_TIER_CLASS_PREFIX + metal : '';

    Array.from(wrapper.classList).forEach((cls) => {
      if (cls.indexOf(WTY_TIER_CLASS_PREFIX) === 0 && cls !== tierClass) {
        wrapper.classList.remove(cls);
      }
    });
    if (tierClass) wrapper.classList.add(tierClass);

    syncWarrantyPrice(wrapper);
  }

  function decorateWarrantyWithin(root) {
    if (!root || !root.querySelectorAll) return;
    const groups = new Set();
    root.querySelectorAll(WARRANTY_INPUT_SELECTOR).forEach((input) => {
      const group = input.closest('.tpo_option-container');
      if (group) groups.add(group);
    });

    groups.forEach((group) => {
      group.classList.add(WARRANTY_GROUP_CLASS);
      group.querySelectorAll('.tpo_radio-button-wrapper').forEach(decorateWarrantyTier);
      group.querySelectorAll('.tpo_option_type_description').forEach(formatWarrantyText);
      group.querySelectorAll('.tpo_option-help-text').forEach((help) => {
        formatWarrantyText(help.querySelector(':scope > span') || help);
      });
    });
  }

  function initWarrantyCover(product) {
    if (!product || product.__mEasifyWarrantyBound) return;
    product.__mEasifyWarrantyBound = true;

    decorateWarrantyWithin(product);

    if (!('MutationObserver' in window)) return;

    // Easify renders / re-renders the group asynchronously. Re-scans are
    // coalesced into one animation frame; our own writes only happen when
    // something differs, so the follow-up pass is a no-op and the loop ends.
    // Class toggles are attribute changes, which aren't observed.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        decorateWarrantyWithin(product);
      });
    });

    observer.observe(product, { childList: true, subtree: true, characterData: true });
  }

  const POWER_SUPPLY_GROUP_REGEX = /power\s+supply\s+upgrade/i;
  const INCLUDED_PSU_LABEL_REGEX = /\b(?:safely\s+included\s+psu|included\s+psu)\b/i;
  const INCLUDED_PSU_WITH_MODEL_REGEX = /\bincluded\s+psu\s+-/i;
  const PSU_GROUP_SELECTOR = '.tpo_option-group_wrapper, .tpo_option-label';
  const PSU_LABEL_SELECTOR = [
    '.tpo_radio-button-wrapper .tpo_option_type_label',
    '.tpo_radio-button-wrapper .tpo_option_label',
  ].join(',');

  function replaceIncludedPsuText(label, replacement) {
    if (!label || INCLUDED_PSU_WITH_MODEL_REGEX.test(label.textContent || '')) return;

    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, svg, .tpo_additional-price, .tpo_badge')) {
          return NodeFilter.FILTER_REJECT;
        }
        return INCLUDED_PSU_LABEL_REGEX.test(node.nodeValue || '')
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      node.nodeValue = node.nodeValue.replace(INCLUDED_PSU_LABEL_REGEX, replacement);
    });
  }

  function updateIncludedPsuLabels(product) {
    const psuModel =
      product && product.dataset ? (product.dataset.easifyPsuModel || '').trim() : '';
    if (!psuModel) return;

    const replacement = `Included PSU - ${psuModel}`;
    product.querySelectorAll(PSU_GROUP_SELECTOR).forEach((group) => {
      const title = group.querySelector(':scope > .tpo_option-label-container');
      const includedInput = group.querySelector(
        'input.tpo_radio-button[value="Included PSU"][name*="Power Supply Upgrade"]'
      );

      if ((!title || !POWER_SUPPLY_GROUP_REGEX.test(title.textContent || '')) && !includedInput) {
        return;
      }

      group.querySelectorAll(PSU_LABEL_SELECTOR).forEach((label) => {
        replaceIncludedPsuText(label, replacement);
      });
    });
  }

  function initIncludedPsuLabels(product) {
    if (!product || product.__mEasifyIncludedPsuBound) return;
    product.__mEasifyIncludedPsuBound = true;

    updateIncludedPsuLabels(product);

    if (!('MutationObserver' in window)) return;

    let scheduled = false;
    const scheduleScan = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        updateIncludedPsuLabels(product);
      });
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          scheduleScan();
          return;
        }

        if (
          mutation.type === 'characterData' &&
          INCLUDED_PSU_LABEL_REGEX.test(mutation.target.nodeValue || '')
        ) {
          scheduleScan();
          return;
        }
      }
    });

    observer.observe(product, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Tags the accessories option group with the CSS hook class
   * `m-easify-accessories-group` so that all Section 13 styles
   * (horizontal tile layout, merged category cards, inline stepper,
   * badge repositioning) are scoped exclusively to this group.
   *
   * Detection strategy: Easify tooltip marker — set the "Tooltip" field
   * of the option group in Easify to exactly "m-easify-accessories-group"
   * (matching ACCESSORIES_GROUP_CLASS). The tooltip renders as a
   * `.tpo_option-tooltip-content` element whose text content is read here.
   *
   * HOW TO RETARGET: if the group changes, update the Easify tooltip
   * content for that group to "m-easify-accessories-group". No CSS or
   * JS code changes are needed — the class name stays the same.
   */
  const ACCESSORIES_GROUP_CLASS = 'm-easify-accessories-group';

  function initAccessoriesGroupTag(product) {
    if (!product) return;

    const scan = () => {
      product.querySelectorAll('.tpo_option-group_wrapper').forEach((wrapper) => {
        if (wrapper.classList.contains(ACCESSORIES_GROUP_CLASS)) {
          wrapper.setAttribute('data-pdp-nav-target', 'peripherals');
          return;
        }

        const marker = wrapper.querySelector('.tpo_option-tooltip-content');
        const isMatch = marker && marker.textContent.trim() === ACCESSORIES_GROUP_CLASS;
        if (isMatch) {
          wrapper.classList.add(ACCESSORIES_GROUP_CLASS);
          wrapper.setAttribute('data-pdp-nav-target', 'peripherals');
          const tooltip = marker.closest('.tpo_option-tooltip');
          if (tooltip) tooltip.style.display = 'none';
          mLog('Tagged accessories group via tooltip marker:', wrapper.id || wrapper.className);
        }
      });
    };

    scan();

    if (!('MutationObserver' in window)) return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        scan();
      });
    });
    observer.observe(product, { childList: true, subtree: true });
  }

  /**
   * Move the product-list "See all" price into the button's text column so it
   * renders directly under the selected product name (matching the curated
   * tile cards above), instead of floating as an absolutely-positioned sibling
   * of the button. Easify updates the price text content in place, so
   * relocating the node once is safe — later selection changes still apply.
   */
  function initPopupButtonPrice(product) {
    if (!product || product.__mEasifyPopupPriceInit) return;
    product.__mEasifyPopupPriceInit = true;

    const movePrice = (button) => {
      const label = button.closest('.tpo_option-label');
      if (!label) return;
      const price = label.querySelector(':scope > .tpo_additional-price.product-list');
      const textWrap = button.querySelector('.tpo_popup-modal-button-text');
      if (!price || !textWrap || price.__mEasifyMoved) return;
      price.__mEasifyMoved = true;
      textWrap.appendChild(price);
    };

    const scan = () => {
      product
        .querySelectorAll('.m-easify-accessories-group .tpo_popup-modal-button.style-1')
        .forEach(movePrice);
    };

    scan();

    if (!('MutationObserver' in window)) return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        scan();
      });
    });
    observer.observe(product, { childList: true, subtree: true });
  }

  function initGroupToggleDelegation(scope) {
    const root = scope || document;
    if (root.__mEasifyGroupToggleBound) return;
    root.__mEasifyGroupToggleBound = true;

    root.addEventListener('click', (event) => {
      const wrapper = event.target.closest('.tpo_option-group_wrapper');
      if (!wrapper) return;

      // Native toggle row — let Easify handle it.
      if (event.target.closest('.tpo_option-label-container')) return;

      // Clicks inside an actual option (radio card, popup button, etc.)
      // belong to that control.
      if (event.target.closest('.tpo_option-container')) return;

      // Anything else interactive a user might land on by accident.
      if (event.target.closest('a, button, input, select, textarea, label, [role="button"]')) {
        return;
      }

      const labelRow = wrapper.querySelector(':scope > .tpo_option-label-container');
      if (labelRow) labelRow.click();
    });
  }

  /**
   * Easify renders the product-list modal (Extra Storage / Keyboard /
   * Monitor picker) at the end of <body> and toggles `display: block`
   * on `.tpo_modal-background` to open it. While the modal is open we
   * lock <html>/<body> scroll and add a click-outside-to-close handler
   * on the overlay. We bind once globally — the modal nodes outlive
   * any product re-init and there is only ever one open at a time.
   */
  function initModalUx() {
    if (document.__mEasifyModalUxBound) return;
    document.__mEasifyModalUxBound = true;

    const SCROLL_LOCK_CLASS = 'm-easify-modal-open';
    const root = document.documentElement;
    const body = document.body;

    const isOpen = (overlay) => {
      if (!overlay) return false;
      // Easify writes inline `display: block` / `display: none`.
      return (overlay.style.display || '').trim().toLowerCase() === 'block';
    };

    const lockScroll = () => {
      if (root.classList.contains(SCROLL_LOCK_CLASS)) return;
      // Compensate for the disappearing scrollbar to avoid a layout shift.
      const sbw = window.innerWidth - root.clientWidth;
      if (sbw > 0) body.style.paddingRight = sbw + 'px';
      root.classList.add(SCROLL_LOCK_CLASS);
      body.classList.add(SCROLL_LOCK_CLASS);
    };

    const unlockScroll = () => {
      root.classList.remove(SCROLL_LOCK_CLASS);
      body.classList.remove(SCROLL_LOCK_CLASS);
      body.style.paddingRight = '';
    };

    const findCloseButton = (overlay) => overlay.querySelector('.tpo_modal-close-btn');

    const closeModal = (overlay) => {
      const closeBtn = findCloseButton(overlay);
      if (closeBtn) {
        closeBtn.click();
      } else {
        // Fallback — match Easify's own close behaviour.
        overlay.style.display = 'none';
      }
    };

    const handleOverlayClick = (event) => {
      const overlay = event.target.closest('.tpo_modal-background');
      if (!overlay || !isOpen(overlay)) return;
      // Only close when the bare overlay is clicked, not the dialog itself.
      if (event.target === overlay) closeModal(overlay);
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      const open = document.querySelector(
        '.tpo_modal-background[style*="display: block"], .tpo_modal-background[style*="display:block"]'
      );
      if (open) closeModal(open);
    };

    const syncScrollLock = () => {
      const anyOpen = document.querySelector(
        '.tpo_modal-background[style*="display: block"], .tpo_modal-background[style*="display:block"]'
      );
      if (anyOpen) lockScroll();
      else unlockScroll();
    };

    // Initial sync — Easify might open the modal before our script runs in
    // some edge cases (e.g. deep-link or back/forward cache restore).
    syncScrollLock();

    document.addEventListener('click', handleOverlayClick, true);
    document.addEventListener('keydown', handleEscape);

    if (!('MutationObserver' in window)) return;

    // Watch for inline `style` flips on any modal overlay, plus modals
    // being added/removed entirely.
    const overlayObserver = new MutationObserver(syncScrollLock);
    document.querySelectorAll('.tpo_modal-background').forEach((overlay) => {
      overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    });

    // Easify can inject the overlay later — observe <body> for newly added
    // modals so we can attach the inline-style observer to them too.
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('tpo_modal-background')) {
            overlayObserver.observe(node, { attributes: true, attributeFilter: ['style'] });
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.tpo_modal-background').forEach((overlay) => {
              overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });
            });
          }
        }
      }
      syncScrollLock();
    });
    bodyObserver.observe(body, { childList: true, subtree: false });
  }

  /* =====================================================================
     Shared "while a modal is open" poller.

     Easify mutates the qty stepper's `input.value` as a property (no
     `input` event, no MutationObserver callback), so a couple of features
     need a periodic reconcile. Rather than each one running its own
     `setInterval` over the whole document for the page's entire lifetime,
     they register a callback here: a SINGLE 200ms interval runs only while
     at least one `.tpo_modal-background` overlay is visible, and stops
     itself the moment they all close.
     ===================================================================== */
  const MODAL_OVERLAY_SELECTOR = '.tpo_modal-background';

  const isModalOverlayOpen = (overlay) =>
    !!overlay && (overlay.style.display || '').trim().toLowerCase() === 'block';

  const modalPollCallbacks = new Set();
  let modalPollTimer = null;
  let modalPollWatcherBound = false;

  const getOpenModalOverlays = () =>
    [...document.querySelectorAll(MODAL_OVERLAY_SELECTOR)].filter(isModalOverlayOpen);

  function stopModalPoll() {
    if (modalPollTimer === null) return;
    clearInterval(modalPollTimer);
    modalPollTimer = null;
  }

  function runModalPoll() {
    const overlays = getOpenModalOverlays();
    if (!overlays.length) {
      stopModalPoll();
      return;
    }
    modalPollCallbacks.forEach((cb) => {
      try {
        cb(overlays);
      } catch (err) {
        mLog('modal poll callback failed', err);
      }
    });
  }

  function startModalPoll() {
    if (modalPollTimer !== null || !getOpenModalOverlays().length) return;
    modalPollTimer = setInterval(runModalPoll, 200);
  }

  /** Wake the poller whenever an overlay's inline `display` flips to block. */
  function ensureModalPollWatcher() {
    if (modalPollWatcherBound) return;
    modalPollWatcherBound = true;

    if (!('MutationObserver' in window)) {
      // No observer support: fall back to a single self-suspending poll that
      // boots itself only when something is already open.
      startModalPoll();
      return;
    }

    const styleObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (
          m.type === 'attributes' &&
          m.attributeName === 'style' &&
          isModalOverlayOpen(m.target)
        ) {
          startModalPoll();
          return;
        }
      }
    });

    const observeOverlay = (overlay) =>
      styleObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });

    document.querySelectorAll(MODAL_OVERLAY_SELECTOR).forEach(observeOverlay);

    // Easify can inject overlays later — observe <body> so we can attach the
    // style observer to newly added modals too.
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('tpo_modal-background')) {
            observeOverlay(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll(MODAL_OVERLAY_SELECTOR).forEach(observeOverlay);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });

    // Cover the edge case where a modal is already open when we bind.
    startModalPoll();
  }

  /** Register a callback invoked with the open overlays on each poll tick. */
  function registerModalPoll(callback) {
    modalPollCallbacks.add(callback);
    ensureModalPollWatcher();
  }

  /**
   * UX layer for the product-list modal items (Extra Storage / Bundle / Monitor).
   *
   * Easify's defaults are confusing:
   *   - only the small image-label toggles selection (the rest of the row is dead),
   *   - the quantity stepper allows the value to drop to 0 but does NOT clear
   *     the checkbox's `data-checked` attribute, so any code that keys off
   *     `data-checked` keeps reporting the row as selected after Easify has
   *     already removed it from the running selection.
   *
   * The visual selected state is driven entirely from CSS (`:has(input:checked)`
   * on the row), so the ring tracks the live `checked` property of the native
   * checkbox — no parallel `data-*` attribute that can drift out of sync.
   *
   * This layer just handles two things:
   *   1. Forwards clicks anywhere on `.tpo_swatches-label_position` (except the
   *      stepper / native interactive controls / the checkbox label itself) to
   *      Easify's image label, so the entire card behaves as the select target.
   *   2. When qty drops to 0 on a checked row, programmatically clicks the
   *      checkbox label so Easify drops the item from its internal selection
   *      AND flips `input.checked` to false. The CSS reacts automatically.
   */
  function initModalItemUx() {
    if (document.__mEasifyModalItemUxBound) return;
    document.__mEasifyModalItemUxBound = true;

    const ITEM_SELECTOR = '.tpo_swatches-label_position';
    const QTY_INPUT_SELECTOR = '.tpo_quantity-selector_input';
    const QTY_BUTTON_SELECTOR = '.tpo_quantity-selector_minus, .tpo_quantity-selector_plus';
    const CHECKBOX_LABEL_SELECTOR = ':scope > label.tpo_shape_square';
    const CHECKBOX_SELECTOR = 'input.tpo_image-swatches-input, input.tpo_color-swatches-input';

    const getCheckbox = (item) => item.querySelector(CHECKBOX_SELECTOR);
    const getCheckboxLabel = (item) => item.querySelector(CHECKBOX_LABEL_SELECTOR);
    const getQtyInput = (item) => item.querySelector(QTY_INPUT_SELECTOR);

    const getQtyValue = (item) => {
      const input = getQtyInput(item);
      if (!input) return 0;
      const v = parseInt(input.value, 10);
      return Number.isFinite(v) ? v : 0;
    };

    // 1. Whole-row click → forward to Easify's image label.
    document.addEventListener('click', (event) => {
      const overlay = event.target.closest('.tpo_modal-background');
      if (!overlay) return;
      const item = event.target.closest(ITEM_SELECTOR);
      if (!item) return;
      // Native handlers below take care of themselves.
      if (event.target.closest('label.tpo_shape_square')) return;
      if (event.target.closest('.tpo_quantity-selector_wrapper')) return;
      if (event.target.closest('a, button, input, textarea, select')) return;

      const label = getCheckboxLabel(item);
      if (label) label.click();
    });

    // qty hits 0 on a checked row → ask Easify to drop the item. We never
    // auto-SELECT in the other direction: the qty input defaults to 1 on
    // unselected rows, so "qty > 0 && !checked" is the resting state for
    // every row in the modal and acting on it would auto-select everything.
    // The CSS reacts to the resulting `input.checked` flip on its own.
    //
    // Important: this only applies to items that actually have a quantity
    // stepper. Radio / single-select modals have no qty input, so
    // `getQtyValue` would return 0 for every row and the poll would happily
    // click the label back off the moment the user picks something.
    const syncFromQty = (item) => {
      if (!item) return;
      const cb = getCheckbox(item);
      if (!cb || !cb.checked) return;
      const qtyInput = getQtyInput(item);
      if (!qtyInput) return;
      if (getQtyValue(item) > 0) return;
      const label = getCheckboxLabel(item);
      if (label) label.click();
    };

    const handleQtyEvent = (event) => {
      if (!event.target.matches || !event.target.matches(QTY_INPUT_SELECTOR)) return;
      const item = event.target.closest(ITEM_SELECTOR);
      // Wait one frame so any clamping Easify does (e.g. snapping back to 1)
      // has settled before we read the value.
      requestAnimationFrame(() => syncFromQty(item));
    };

    ['input', 'change', 'keyup', 'blur'].forEach((evt) => {
      document.addEventListener(evt, handleQtyEvent, true);
      document.addEventListener(evt, handleQtyEvent);
    });

    // Stepper +/- mutates the input via Easify's JS without firing input/change
    // on the field itself, so we sync from the click directly. Two RAFs cover
    // both sync and async update paths inside Easify.
    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest(QTY_BUTTON_SELECTOR);
        if (!btn) return;
        const item = btn.closest(ITEM_SELECTOR);
        if (!item) return;
        requestAnimationFrame(() => {
          syncFromQty(item);
          requestAnimationFrame(() => syncFromQty(item));
        });
      },
      true
    );

    // Safety net: reconcile items where qty has gone stale relative to the
    // checkbox state. Catches the case where Easify mutates `input.value` as
    // a property (no `input` event fires) outside any of our event paths.
    // Runs only while a modal is open (see registerModalPoll) and only scans
    // the open overlays — never the whole document.
    registerModalPoll((overlays) => {
      overlays.forEach((overlay) => {
        overlay.querySelectorAll(ITEM_SELECTOR).forEach(syncFromQty);
      });
    });
  }

  /**
   * Easify disables the Confirm button whenever the running modal selection
   * fails the picker's "min N" rule, which means a customer who opens the
   * modal with an existing selection physically cannot clear it: deselecting
   * drops them below the minimum, Easify greys Confirm out, and they're
   * stuck.
   *
   * Fix: when the modal opens with an existing selection, remember the
   * starting "size" of that selection (sum of quantities for stepper rows,
   * count of checked rows for radios/checkboxes). For the rest of the
   * modal session, force-enable Confirm only while the current size is
   * `<=` the starting size — i.e. the user is editing downward. If they
   * push the selection PAST the starting size we leave Easify's validation
   * alone, so the "maximum N items" rule still greys Confirm out when it
   * should.
   *
   * As soon as the modal closes the override clears, so a fresh open with
   * no prior selection still gets Easify's default validation behaviour.
   */
  function initModalConfirmFix() {
    if (document.__mEasifyModalConfirmFixBound) return;
    document.__mEasifyModalConfirmFixBound = true;

    const ITEM_SELECTOR = '.tpo_swatches-label_position';
    const ITEM_INPUT_SELECTOR = 'input.tpo_image-swatches-input, input.tpo_color-swatches-input';
    const QTY_INPUT_SELECTOR = '.tpo_quantity-selector_input';
    const CONFIRM_SELECTOR = '.button-modal.primary';

    const isOpen = (overlay) => (overlay.style.display || '').trim().toLowerCase() === 'block';

    /**
     * Total "size" of the current selection in the modal — sum of quantities
     * for checked stepper rows, +1 per checked row without a stepper.
     */
    const getSelectionSize = (overlay) => {
      let size = 0;
      overlay.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
        const cb = item.querySelector(ITEM_INPUT_SELECTOR);
        if (!cb || !cb.checked) return;
        const qtyInput = item.querySelector(QTY_INPUT_SELECTOR);
        if (qtyInput) {
          const v = parseInt(qtyInput.value, 10);
          if (Number.isFinite(v) && v > 0) size += v;
        } else {
          size += 1;
        }
      });
      return size;
    };

    /** Records the selection size each modal had at open-time. */
    const initialSize = new WeakMap();

    const enableConfirm = (overlay) => {
      const btn = overlay.querySelector(CONFIRM_SELECTOR);
      if (!btn) return;
      if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
      if (btn.classList.contains('disabled')) btn.classList.remove('disabled');
    };

    /**
     * Decide whether the user is currently EDITING DOWNWARD relative to the
     * snapshot taken when the modal opened. If so, we override Easify's
     * disabled state. If they've grown the selection past the snapshot we
     * back off and let Easify enforce the max.
     */
    const shouldOverride = (overlay) => {
      if (!initialSize.has(overlay)) return false;
      const start = initialSize.get(overlay);
      if (start <= 0) return false;
      return getSelectionSize(overlay) <= start;
    };

    const reconcileConfirm = (overlay) => {
      if (shouldOverride(overlay)) enableConfirm(overlay);
    };

    const handleOpen = (overlay) => {
      const size = getSelectionSize(overlay);
      initialSize.set(overlay, size);
      if (size > 0) {
        // Force-sync once on open and again after a frame in case Easify
        // rebinds its disabled state during the modal's render pass.
        enableConfirm(overlay);
        requestAnimationFrame(() => enableConfirm(overlay));
      }
    };

    const handleClose = (overlay) => {
      initialSize.delete(overlay);
    };

    const overlayState = new WeakMap();
    const visibilityObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'style') continue;
        const overlay = m.target;
        const open = isOpen(overlay);
        const wasOpen = overlayState.get(overlay) === true;
        if (open === wasOpen) continue;
        overlayState.set(overlay, open);
        if (open) handleOpen(overlay);
        else handleClose(overlay);
      }
    });

    /**
     * Subtree observer keeps Confirm enabled whenever Easify tries to
     * disable it AND the user is still editing within the snapshot size.
     * Also fires reconcile on selection / qty mutations so the override
     * disengages the moment the user grows past the snapshot.
     */
    const subtreeObserver = new MutationObserver((mutations) => {
      const overlays = new Set();
      for (const m of mutations) {
        const target = m.target;
        if (!(target instanceof Element)) continue;
        const overlay = target.closest('.tpo_modal-background');
        if (overlay) overlays.add(overlay);
      }
      overlays.forEach(reconcileConfirm);
    });

    const bindOverlay = (overlay) => {
      if (!overlay || overlay.__mEasifyConfirmFixBound) return;
      overlay.__mEasifyConfirmFixBound = true;
      visibilityObserver.observe(overlay, {
        attributes: true,
        attributeFilter: ['style'],
      });
      subtreeObserver.observe(overlay, {
        subtree: true,
        childList: true,
        attributes: true,
        // `value` so qty changes (when Easify reflects them as attributes)
        // wake us up too; `checked` for radio/checkbox flips.
        attributeFilter: ['disabled', 'class', 'value', 'checked'],
      });
      overlayState.set(overlay, isOpen(overlay));
      if (isOpen(overlay)) handleOpen(overlay);
    };

    document.querySelectorAll('.tpo_modal-background').forEach(bindOverlay);

    // Easify mutates `input.value` as a property on the qty stepper, which
    // does not fire a MutationObserver. A shared poll — active only while a
    // modal is open (see registerModalPoll) — keeps the override in sync with
    // what the user has actually typed/clicked. `reconcileConfirm` is a no-op
    // unless the overlay has an open-time snapshot, so it's safe per tick.
    registerModalPoll((overlays) => {
      overlays.forEach(reconcileConfirm);
    });

    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('tpo_modal-background')) {
            bindOverlay(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.tpo_modal-background').forEach(bindOverlay);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  /* =====================================================================
     Shared DSL helpers for the Easify "Options details link" field.
     Used by BOTH the product-list modal enhancement and the radio-option
     (outside-the-modal) enhancement, so they live at module scope.
     ===================================================================== */

  /**
   * Easify treats the link field as a URL and:
   *   1. Prepends "https://" if the value doesn't start with a protocol.
   *   2. Lowercases the entire string.
   *   3. Does NOT HTML-encode `"` — causing the href attribute to close early.
   *      Use `'` (apostrophe) as the inch mark instead (e.g. 27' not 27").
   *   4. May URL-encode or truncate at spaces.
   *      Use `~` as a safe word-separator where spaces are needed
   *      (e.g. desc=exceptional~colour~accuracy); %20 works too.
   *
   * Examples:
   *   easify:filter=1080p|specs=23.8',full~hd,100hz,va
   *   easify:filter=4k|specs=31.5',4k,60hz,oled|desc=…|gpu=rtx5070,rtx5080
   */
  function parseEasifyMeta(href) {
    if (!href) return null;
    let raw = href;
    // Strip the https:// Easify automatically prepends.
    if (raw.startsWith('https://')) raw = raw.slice('https://'.length);
    if (!raw.startsWith('easify:')) return null;

    let dsl = raw.slice('easify:'.length);
    // Decode any URL-encoding Easify may have applied (e.g. %20 → space,
    // %7C → |). If the string isn't valid percent-encoding, leave it as-is.
    try {
      dsl = decodeURIComponent(dsl);
    } catch {}

    const result = {};
    dsl.split('|').forEach((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return;
      // Replace ~ with space: allows multi-word values without needing %20.
      const val = pair
        .slice(eq + 1)
        .replace(/~/g, ' ')
        .trim();
      result[pair.slice(0, eq).trim()] = val;
    });
    return result;
  }

  /**
   * Restore correct casing for spec chip text that Easify has lowercased.
   * Handles common monitor / display abbreviations plus the Hz suffix pattern.
   */
  const SPEC_CASE_MAP = {
    ips: 'IPS',
    'ips black': 'IPS Black',
    'nano ips': 'Nano IPS',
    va: 'VA',
    mva: 'MVA',
    tn: 'TN',
    pls: 'PLS',
    oled: 'OLED',
    qled: 'QLED',
    amoled: 'AMOLED',
    'mini-oled': 'Mini-OLED',
    'mini oled': 'Mini-OLED',
    hd: 'HD',
    'full hd': 'Full HD',
    'full hd+': 'Full HD+',
    fhd: 'FHD',
    qhd: 'QHD',
    wqhd: 'WQHD',
    uhd: 'UHD',
    '4k': '4K',
    '2k': '2K',
    '8k': '8K',
    '1080p': '1080p',
    '1440p': '1440p',
    '4320p': '4320p',
    hdr: 'HDR',
    hdr10: 'HDR10',
    'hdr10+': 'HDR10+',
    sdr: 'SDR',
    dcip3: 'DCI-P3',
    'dci-p3': 'DCI-P3',
    hdmi: 'HDMI',
    dp: 'DP',
    'usb-c': 'USB-C',
    'g-sync': 'G-Sync',
    freesync: 'FreeSync',
    curved: 'Curved',
    // Peripherals (keyboard / mouse / bundle pickers)
    usb: 'USB',
    uk: 'UK',
    dpi: 'DPI',
    ghz: 'GHz',
    tkl: 'TKL',
    rgb: 'RGB',
    qwerty: 'QWERTY',
    iso: 'ISO',
    mlx: 'MLX',
    mgx: 'MGX',
    mmo: 'MMO',
    anc: 'ANC',
    bluetooth: 'Bluetooth',
    hyperspeed: 'HyperSpeed',
    playstation: 'PlayStation',
    xbox: 'Xbox',
    pcie: 'PCIe',
    nvme: 'NVMe',
    sata: 'SATA',
    ssd: 'SSD',
  };

  function formatSpecText(str) {
    const trimmed = str.trim();
    const lower = trimmed.toLowerCase();
    // Whole-string match wins first so multi-word entries (e.g. "ips black"
    // → "IPS Black", "full hd" → "Full HD") keep their exact casing.
    if (SPEC_CASE_MAP[lower]) return SPEC_CASE_MAP[lower];
    // Otherwise restore casing word-by-word: known acronyms anywhere in the
    // chip are uppercased, numeric Hz suffixes are fixed, the first word is
    // capitalised and the rest stay lowercase (sentence case).
    return trimmed
      .split(/\s+/)
      .map((word, i) => {
        const wl = word.toLowerCase();
        if (SPEC_CASE_MAP[wl]) return SPEC_CASE_MAP[wl];
        const hz = word.replace(/(\d+)hz\b/gi, '$1Hz');
        if (hz !== word) return hz;
        // Uppercase the magnitude "k" suffix on DPI / polling values:
        // "30k" → "30K", "8k" → "8K".
        const k = word.replace(/(\d+)k\b/gi, '$1K');
        if (k !== word) return k;
        // Uppercase storage / speed units attached to a digit, preserving a
        // trailing "/s": "1tb" → "1TB", "550mb/s" → "550MB/s", "14.9gb/s" →
        // "14.9GB/s". The leading digit guard keeps it from touching plain
        // words that merely contain these letters (e.g. "number", "membrane").
        const unit = word.replace(
          /(\d)(gb|tb|mb|kb)(\/s)?\b/gi,
          (_m, digit, u, perSec) => digit + u.toUpperCase() + (perSec || '')
        );
        if (unit !== word) return unit;
        return i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
      })
      .join(' ');
  }

  /** Build the spec-chip row element from a raw "specs" DSL value, or null. */
  function buildSpecChips(specsValue) {
    const specs = String(specsValue || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!specs.length) return null;
    const chips = document.createElement('div');
    chips.className = 'm-easify-spec-chips';
    specs.forEach((spec) => {
      const chip = document.createElement('span');
      chip.className = 'm-easify-spec-chip';
      chip.textContent = formatSpecText(spec);
      chips.appendChild(chip);
    });
    return chips;
  }

  /** Build the description paragraph element from a raw "desc" value, or null. */
  function buildItemDesc(descValue) {
    const text = String(descValue || '').trim();
    if (!text) return null;
    const desc = document.createElement('p');
    desc.className = 'm-easify-item-desc';
    // Restore sentence-level capitalisation (Easify lowercases the full href).
    desc.textContent = text.charAt(0).toUpperCase() + text.slice(1);
    return desc;
  }

  /* =====================================================================
     GPU-aware visibility.
     The PC's GPU chipset is exposed by m-main-product.liquid as
     `data-easify-gpu` on the `.product` element. Each monitor option encodes
     the GPUs it's recommended for via `gpu=` in its DSL link field. A row
     that doesn't match is hidden via the `m-easify-gpu-hidden` class
     (CSS `display:none !important`) so neither Easify's own search nor our
     filter pills can re-show it.

     Tokens & rules:
       - A row with NO `gpu=` tag is UNIVERSAL — always shown, any PC.
       - The special token `default` marks a row as a fallback pick: it is
         shown only when the PC's GPU matches nothing else (see below).
       - If nothing in a list is `gpu=`-tagged (e.g. a RAM group), the list
         is left untouched.
       - No-match handling differs per surface:
           · Modal (browse-all): falls back to showing ALL monitors.
           · Outside radio (curated): falls back to `default`-tagged rows,
             or ALL if none are flagged — never an empty section.
     ===================================================================== */
  const GPU_HIDDEN_CLASS = 'm-easify-gpu-hidden';
  const GPU_DEFAULT_TOKEN = 'default';

  /**
   * Normalise a GPU token for comparison: lowercase, drop brand words, then
   * strip every non-alphanumeric character. Brand stripping lets authors write
   * the short form (`rtx5070ti`) instead of mirroring the full chipset string
   * ("GeForce RTX 5070 Ti"), which is an easy silent-mismatch footgun.
   *   "GeForce RTX 5070 Ti"  → "rtx5070ti"
   *   "Radeon RX 9070 XT"    → "rx9070xt"
   *   "GeForce RTX 3080 10GB" → "rtx308010gb"
   */
  function normalizeGpuToken(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/\b(?:geforce|radeon|nvidia|amd|intel|arc)\b/g, ' ')
      .replace(/[^a-z0-9]/g, '');
  }

  /** The current PC's normalised GPU token, or '' when unknown. */
  function getPcGpuToken() {
    const product = document.querySelector('.product[data-easify-gpu]');
    if (!product) {
      if (!getPcGpuToken._warnedNoProduct) {
        getPcGpuToken._warnedNoProduct = true;
        mLog('getPcGpuToken: no .product[data-easify-gpu] found → filtering disabled');
      }
      return '';
    }
    const raw = product.dataset.easifyGpu || '';
    const token = normalizeGpuToken(raw);
    // High-signal misconfig: attribute present but normalises to nothing.
    if (raw && !token && !getPcGpuToken._warnedEmpty) {
      getPcGpuToken._warnedEmpty = true;
      mLog('getPcGpuToken: data-easify-gpu present but empty after normalise', { raw });
    }
    return token;
  }

  /** Parsed, normalised list of GPU tokens a DSL meta object is tagged for. */
  function gpuTokensFromMeta(meta) {
    if (!meta || !meta.gpu) return [];
    return meta.gpu.split(',').map(normalizeGpuToken).filter(Boolean);
  }

  /**
   * Parsed, normalised list of {token, slot, role} entries from a DSL `gpu=`
   * value. Handles the `gpu=rtx5090:1:top~pick,rtx5080:2` encoding format
   * (`token:slot:role`, slot and role both optional). The colon-separated
   * parts MUST be split off before normalising so that `normalizeGpuToken`
   * never sees the colon (it would strip it and silently merge the token with
   * the digit, breaking matching). `~` has already been converted to a space
   * by `parseEasifyMeta`, so role arrives space-separated.
   *   "rtx5090:1:top~pick" → { token: "rtx5090", slot: 1,   role: "top pick" }
   *   "rtx5080:2"          → { token: "rtx5080", slot: 2,   role: "" }
   *   "default"            → { token: "default", slot: NaN, role: "" }
   */
  function gpuEntriesFromMeta(meta) {
    if (!meta || !meta.gpu) return [];
    return meta.gpu
      .split(',')
      .map((raw) => {
        const parts = raw.split(':');
        return {
          token: normalizeGpuToken(parts[0]),
          slot: parts.length > 1 ? parseInt(parts[1], 10) : NaN,
          // Re-join any stray colons back into the role (it shouldn't contain
          // one, but this degrades gracefully rather than truncating).
          role: parts.length > 2 ? parts.slice(2).join(':').trim() : '',
        };
      })
      .filter((e) => e.token);
  }

  /**
   * The current PC's GPU as a human-readable display string (brand prefix
   * stripped, original casing preserved), for use in badge copy.
   *   "GeForce RTX 5070"  → "RTX 5070"
   *   "Radeon RX 9070 XT" → "RX 9070 XT"
   */
  function gpuDisplayName(raw) {
    return String(raw || '')
      .replace(/\b(?:geforce|radeon|nvidia|amd)\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  /** The current PC's raw (unnormalised) GPU chipset string, or ''. */
  function getPcGpuRaw() {
    const product = document.querySelector('.product[data-easify-gpu]');
    return product ? product.dataset.easifyGpu || '' : '';
  }

  /**
   * Brand casing for badge role words Easify has lowercased. SPEC_CASE_MAP
   * already covers display abbreviations (4K, OLED, IPS…); this adds the
   * monitor brands that show up in marketing copy.
   */
  const BRAND_CASE_MAP = {
    koorui: 'KOORUI',
    asrock: 'ASRock',
    aoc: 'AOC',
    lg: 'LG',
    msi: 'MSI',
    asus: 'ASUS',
    acer: 'Acer',
    dell: 'Dell',
    hp: 'HP',
    samsung: 'Samsung',
    gigabyte: 'Gigabyte',
    philips: 'Philips',
    benq: 'BenQ',
    viewsonic: 'ViewSonic',
  };

  /** Restore casing for a single badge-role word (sentence-case aware). */
  function formatBadgeWord(word, isFirst) {
    const lower = word.toLowerCase();
    if (SPEC_CASE_MAP[lower]) return SPEC_CASE_MAP[lower];
    if (BRAND_CASE_MAP[lower]) return BRAND_CASE_MAP[lower];
    const hz = lower.replace(/(\d+)hz\b/g, '$1Hz');
    if (hz !== lower) return hz;
    if (isFirst) return word.charAt(0).toUpperCase() + word.slice(1);
    return word; // keep lowercase → sentence case
  }

  /**
   * Reconstruct readable casing for the lowercased badge role phrase.
   *   "top 4k oled pick" → "Top 4K OLED pick"
   *   "koorui value pick" → "KOORUI value pick"
   */
  function formatBadgeRole(role) {
    const words = String(role || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) return '';
    return words.map((w, i) => formatBadgeWord(w, i === 0)).join(' ');
  }

  /**
   * Build the per-pick badge ("Top pick for RTX 5070") from a role and the
   * current PC's GPU display name, or null when there's no role text.
   */
  function buildPickBadge(role, gpuDisplay) {
    const roleText = formatBadgeRole(role);
    if (!roleText) return null;
    const badge = document.createElement('span');
    badge.className = 'm-easify-pick-badge tpo_badge';
    // Role-based modifier (e.g. "top pick" → "m-easify-pick-badge--top-pick")
    // so CSS can colour each pick type independently.
    const roleSlug = String(role || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (roleSlug) badge.classList.add(`m-easify-pick-badge--${roleSlug}`);
    badge.textContent = gpuDisplay ? `${roleText} for ${gpuDisplay}` : roleText;
    return badge;
  }

  /**
   * Apply GPU visibility across a set of rows.
   * @param {Element[]} rows           The candidate rows.
   * @param {(row:Element)=>string[]} getTokens  Returns a row's GPU tokens.
   * @param {{useDefaultFallback?: boolean}} [opts]  When true, a no-match
   *   situation falls back to `default`-tagged rows (then ALL). Used by the
   *   curated outside picks; the modal omits it to fall back to ALL.
   */
  function applyGpuVisibility(rows, getTokens, opts) {
    const useDefaultFallback = !!(opts && opts.useDefaultFallback);
    const pcGpu = getPcGpuToken();
    const surface = useDefaultFallback ? 'radio (outside)' : 'modal';

    // Clear any previous hidden state first so re-runs are idempotent.
    rows.forEach((row) => row.classList.remove(GPU_HIDDEN_CLASS));

    const tagged = rows.filter((row) => getTokens(row).length > 0);
    if (!tagged.length) {
      mLog(`applyGpuVisibility[${surface}]: no gpu-tagged rows → untouched`, {
        total: rows.length,
      });
      return; // Nothing GPU-tagged (e.g. a RAM group).
    }

    const matchesPc = (row) => !!pcGpu && getTokens(row).includes(pcGpu);
    const matches = tagged.filter(matchesPc);

    if (matches.length) {
      // Normal path: hide tagged rows that aren't for this PC's GPU.
      let hidden = 0;
      tagged.forEach((row) => {
        if (!matchesPc(row)) {
          row.classList.add(GPU_HIDDEN_CLASS);
          hidden += 1;
        }
      });
      mLog(`applyGpuVisibility[${surface}]: matched pcGpu`, {
        pcGpu,
        total: rows.length,
        tagged: tagged.length,
        matches: matches.length,
        hidden,
        shown: rows.length - hidden,
      });
      return;
    }

    // No GPU match (PC GPU unknown, or it matched nothing).
    if (useDefaultFallback) {
      const isDefault = (row) => getTokens(row).includes(GPU_DEFAULT_TOKEN);
      const defaults = tagged.filter(isDefault);
      if (defaults.length) {
        let hidden = 0;
        tagged.forEach((row) => {
          if (!isDefault(row)) {
            row.classList.add(GPU_HIDDEN_CLASS);
            hidden += 1;
          }
        });
        mLog(`applyGpuVisibility[${surface}]: no match → default fallback`, {
          pcGpu: pcGpu || '(unknown)',
          defaults: defaults.length,
          hidden,
        });
        return;
      }
    }

    // Last resort: show everything, so a list/section is never empty.
    mLog(`applyGpuVisibility[${surface}]: no match → show all (fallback)`, {
      pcGpu: pcGpu || '(unknown)',
      total: rows.length,
      tagged: tagged.length,
    });
  }

  /**
   * Modal meta enhancement — reads a structured DSL from the Easify
   * "Options details link" field (href) and transforms it into:
   *   - Filter pill bar (tab-style resolution/category filters)
   *   - Spec chips (small labeled tags: size, panel type, refresh rate…)
   *   - Plain-text description line (fallback for non-chip product types)
   *   - GPU-aware visibility (hides monitors not recommended for this PC)
   *
   * All keys are optional; a row with no "easify:" prefix is left untouched.
   * The original <a> element is hidden; Easify never navigates to the href.
   */
  function initModalMetaEnhancement() {
    if (document.__mEasifyMetaEnhancementBound) return;
    document.__mEasifyMetaEnhancementBound = true;

    /**
     * Enhance a single product-list row with chips / desc from its DSL href.
     * Idempotent — already-enhanced rows are skipped.
     */
    function enhanceItem(item) {
      if (item.__mEasifyMetaEnhanced) return;
      item.__mEasifyMetaEnhanced = true;

      const anchor = item.querySelector('a.tpo_url_value.tpo_view_more');
      if (!anchor) return;

      const meta = parseEasifyMeta(anchor.getAttribute('href') || '');
      if (!meta) return;

      anchor.style.display = 'none';

      const labelWrapper = item.querySelector('.tpo_option_type_label_wrapper');
      // The price element (.tpo_additional-price) lives inside .tpo-swatch-option-label.
      // Inserting spec chips / desc before it achieves the design order:
      //   product name → chips (or desc) → price
      const swatchLabel = item.querySelector('.tpo-swatch-option-label');
      const priceEl = swatchLabel ? swatchLabel.querySelector('.tpo_additional-price') : null;

      const insertBeforePriceOrAppend = (node) => {
        if (priceEl) priceEl.parentNode.insertBefore(node, priceEl);
        else if (labelWrapper) labelWrapper.appendChild(node);
      };

      const chips = buildSpecChips(meta.specs);
      if (chips) insertBeforePriceOrAppend(chips);

      const desc = buildItemDesc(meta.desc);
      if (desc) insertBeforePriceOrAppend(desc);

      if (meta.filter) item.dataset.mEasifyFilter = meta.filter.toLowerCase();

      const gpuTokens = gpuTokensFromMeta(meta);
      if (gpuTokens.length) item.dataset.mEasifyGpu = gpuTokens.join(',');

      mLog('enhanceItem[modal]:', { meta, filter: item.dataset.mEasifyFilter, gpu: gpuTokens });
    }

    const itemGpuTokens = (item) => (item.dataset.mEasifyGpu || '').split(',').filter(Boolean);

    function getActiveFilter(overlay) {
      const active = overlay.querySelector('.m-easify-filter-pill--active');
      return active ? active.dataset.filter || '' : '';
    }

    function applyFilter(overlay, filterValue) {
      overlay.querySelectorAll('.tpo_swatches-label_position').forEach((item) => {
        // GPU filtering wins — never re-show a row hidden for this PC's GPU.
        if (item.classList.contains(GPU_HIDDEN_CLASS)) return;
        const match = !filterValue || (item.dataset.mEasifyFilter || '') === filterValue;
        if (match) {
          // Remove our own override; the stylesheet's display:grid !important takes over.
          item.style.removeProperty('display');
        } else {
          // Must use !important to beat the display:grid !important in m-easify.css.
          item.style.setProperty('display', 'none', 'important');
        }
      });
    }

    function buildFilterBar(overlay, filters) {
      const existing = overlay.querySelector('.m-easify-filter-bar');
      if (existing) existing.remove();
      if (!filters.length) return;

      const bar = document.createElement('div');
      bar.className = 'm-easify-filter-bar';
      bar.setAttribute('role', 'group');
      bar.setAttribute('aria-label', 'Filter options');

      const allPill = document.createElement('button');
      allPill.type = 'button';
      allPill.className = 'm-easify-filter-pill m-easify-filter-pill--active';
      allPill.dataset.filter = '';
      allPill.textContent = 'All';
      bar.appendChild(allPill);

      filters.forEach((f) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'm-easify-filter-pill';
        pill.dataset.filter = f;
        pill.textContent = f.toUpperCase();
        bar.appendChild(pill);
      });

      bar.addEventListener('click', (e) => {
        const pill = e.target.closest('.m-easify-filter-pill');
        if (!pill) return;
        bar.querySelectorAll('.m-easify-filter-pill').forEach((p) => {
          p.classList.toggle('m-easify-filter-pill--active', p === pill);
        });
        applyFilter(overlay, pill.dataset.filter || '');
      });

      const filterWrapper = overlay.querySelector('.tpo_modal-filter-wrapper');
      if (filterWrapper) {
        filterWrapper.appendChild(bar);
      } else {
        const content = overlay.querySelector('.tpo_modal-content');
        if (content) content.insertBefore(bar, content.firstChild);
      }
    }

    /**
     * Enhance all items in the overlay and rebuild the filter bar.
     * Called on every modal open (items may be the same DOM nodes reused by
     * Easify, so enhanceItem is idempotent; filter bar is always rebuilt fresh).
     */
    function enhanceModal(overlay) {
      const items = [...overlay.querySelectorAll('.tpo_swatches-label_position')];
      items.forEach(enhanceItem);

      // Apply GPU visibility BEFORE building the pill bar so the pills only
      // reflect resolutions available within this PC's monitor subset.
      applyGpuVisibility(items, itemGpuTokens);

      const filters = new Set();
      items.forEach((item) => {
        if (item.classList.contains(GPU_HIDDEN_CLASS)) return;
        if (item.dataset.mEasifyFilter) filters.add(item.dataset.mEasifyFilter);
      });
      buildFilterBar(overlay, [...filters]);
    }

    const isOpen = (overlay) => (overlay.style.display || '').trim().toLowerCase() === 'block';

    const overlayState = new WeakMap();

    function handleOpen(overlay) {
      // Reset any visibility state left over from a previous filter session.
      overlay.querySelectorAll('.tpo_swatches-label_position').forEach((item) => {
        item.style.removeProperty('display');
      });
      // Wait one frame for Easify to finish rendering items before we scan.
      requestAnimationFrame(() => enhanceModal(overlay));
    }

    const visibilityObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'style') continue;
        const overlay = m.target;
        const open = isOpen(overlay);
        if (overlayState.get(overlay) === open) continue;
        overlayState.set(overlay, open);
        if (open) handleOpen(overlay);
      }
    });

    // Watch for items that Easify may render after the modal opens.
    const itemObservers = new WeakMap();

    function bindItemObserver(overlay) {
      if (itemObservers.has(overlay)) return;
      let scheduled = false;
      const observer = new MutationObserver(() => {
        if (!isOpen(overlay) || scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          // Only enhance new items; filter bar is not rebuilt to preserve
          // the user's active filter selection.
          const activeFilter = getActiveFilter(overlay);
          const items = [...overlay.querySelectorAll('.tpo_swatches-label_position')];
          const hasNew = items.some((item) => !item.__mEasifyMetaEnhanced);
          if (!hasNew) return;
          items.forEach(enhanceItem);
          // Re-evaluate GPU visibility now that new rows exist.
          applyGpuVisibility(items, itemGpuTokens);
          items.forEach((item) => {
            if (item.classList.contains(GPU_HIDDEN_CLASS)) return;
            if (activeFilter && (item.dataset.mEasifyFilter || '') !== activeFilter) {
              item.style.setProperty('display', 'none', 'important');
            }
          });
        });
      });
      observer.observe(overlay, { childList: true, subtree: true });
      itemObservers.set(overlay, observer);
    }

    function bindOverlay(overlay) {
      if (!overlay || overlay.__mEasifyMetaBound) return;
      overlay.__mEasifyMetaBound = true;
      overlayState.set(overlay, isOpen(overlay));
      visibilityObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });
      bindItemObserver(overlay);
      if (isOpen(overlay)) handleOpen(overlay);
    }

    document.querySelectorAll('.tpo_modal-background').forEach(bindOverlay);

    const bodyObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('tpo_modal-background')) {
            bindOverlay(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.tpo_modal-background').forEach(bindOverlay);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  /**
   * Radio-option enhancement (monitors shown OUTSIDE the modal).
   *
   * The curated "top picks" radio option carries the same DSL in its
   * "Options details link" field as the modal rows. For each radio option we:
   *   - hide the fake "View" anchor,
   *   - render spec chips / description from the DSL,
   *   - tag it with its GPU compatibility,
   * then hide the options that aren't recommended for this PC's GPU.
   *
   * Radio options with no DSL link (RAM, warranty, OS, …) are never touched.
   */
  function initRadioMonitorEnhancement(product) {
    if (!product || product.__mEasifyRadioEnhanced) return;
    product.__mEasifyRadioEnhanced = true;

    // data-mEasifyGpu stores "token:slot" pairs (slot is optional).
    // Strip the :slot suffix before comparison so applyGpuVisibility only sees tokens.
    const radioGpuTokens = (wrapper) =>
      (wrapper.dataset.mEasifyGpu || '')
        .split(',')
        .map((e) => e.split(':')[0])
        .filter(Boolean);

    const enhanceRadio = (wrapper) => {
      if (wrapper.__mEasifyRadioItemDone) return;

      const anchor = wrapper.querySelector('a.tpo_url_value.tpo_view_more');
      if (!anchor) return; // No DSL link on this option — leave it alone.

      const meta = parseEasifyMeta(anchor.getAttribute('href') || '');
      if (!meta) return;

      wrapper.__mEasifyRadioItemDone = true;
      anchor.style.display = 'none';

      // Insert chips/desc after the title+price line, before any native
      // description, within the option's content column.
      const labelWrapper = wrapper.querySelector('.tpo_option_type_label_wrapper');
      const contentColumn =
        (labelWrapper && labelWrapper.parentNode) ||
        wrapper.querySelector('.tpo_next_option_type') ||
        anchor.parentNode;
      const description = wrapper.querySelector('.tpo_option_type_description');

      const insertNode = (node) => {
        if (labelWrapper) {
          labelWrapper.appendChild(node);
        } else if (description && description.parentNode === contentColumn) {
          contentColumn.insertBefore(node, description);
        } else {
          contentColumn.appendChild(node);
        }
      };

      const chips = buildSpecChips(meta.specs);
      if (chips) insertNode(chips);

      const desc = buildItemDesc(meta.desc);
      if (desc) insertNode(desc);

      // Store entries as "token:slot" (slot omitted when absent) so the rescan
      // can later read back per-GPU slot numbers without re-parsing the DSL.
      // Role is intentionally NOT stored here — the badge is rendered below
      // while `meta` is in scope, keeping the data attribute parser simple.
      const gpuEntries = gpuEntriesFromMeta(meta);
      if (gpuEntries.length) {
        wrapper.dataset.mEasifyGpu = gpuEntries
          .map((e) => (Number.isFinite(e.slot) ? `${e.token}:${e.slot}` : e.token))
          .join(',');
      }

      // Per-pick badge: shown only when an entry's GPU matches this PC's GPU
      // exactly (fallback/default rows carry no "Top pick for …" claim). The
      // GPU name is taken from the chipset metafield so its casing is correct
      // even though Easify lowercases the href the role comes from.
      const pcGpu = getPcGpuToken();
      const matchedEntry = pcGpu ? gpuEntries.find((e) => e.token === pcGpu && e.role) : null;
      if (matchedEntry) {
        const badgeHost = wrapper.querySelector('label.tpo_') || wrapper;
        if (badgeHost && !badgeHost.querySelector(':scope > .m-easify-pick-badge')) {
          const badge = buildPickBadge(matchedEntry.role, gpuDisplayName(getPcGpuRaw()));
          if (badge) badgeHost.appendChild(badge);
        }
      }

      mLog('enhanceRadio[outside]:', { meta, gpu: gpuEntries, pcGpu });
    };

    const rescan = () => {
      const wrappers = [...product.querySelectorAll('.tpo_radio-button-wrapper')];
      if (!wrappers.length) return;
      wrappers.forEach(enhanceRadio);
      // Curated outside picks: no GPU match → fall back to `default` rows.
      applyGpuVisibility(wrappers, radioGpuTokens, { useDefaultFallback: true });

      // Reorder visible picks by their per-GPU slot number using CSS `order`.
      // Setting style.order is an `attributes` mutation — the observer above
      // only watches `childList`, so this cannot re-trigger the observer.
      const pcGpu = getPcGpuToken();
      wrappers.forEach((wrapper) => {
        // Walk up from .tpo_radio-button-wrapper to the direct grid-item child
        // of .tpo_radio-buttons-wrapper (usually .container-wrap-selection).
        let gridItem = wrapper;
        while (
          gridItem.parentElement &&
          !gridItem.parentElement.classList.contains('tpo_radio-buttons-wrapper')
        ) {
          gridItem = gridItem.parentElement;
        }
        // Guard: only mutate when we actually found the grid container.
        if (
          !gridItem.parentElement ||
          !gridItem.parentElement.classList.contains('tpo_radio-buttons-wrapper')
        ) {
          return;
        }

        // Look up the slot for this PC's GPU in the stored "token:slot" data.
        let slot = NaN;
        if (pcGpu) {
          (wrapper.dataset.mEasifyGpu || '').split(',').forEach((entry) => {
            const sep = entry.lastIndexOf(':');
            if (sep !== -1 && entry.slice(0, sep) === pcGpu) {
              slot = parseInt(entry.slice(sep + 1), 10);
            }
          });
        }

        // Rows with no matching slot go last; rows sharing the same slot keep
        // their original DOM order relative to each other (CSS tie-break).
        gridItem.style.order = Number.isFinite(slot) ? String(slot) : '999';
      });
    };

    rescan();

    if (!('MutationObserver' in window)) return;

    // Easify injects / re-renders option groups asynchronously; re-scan on a
    // coalesced animation frame. Our own insertions are guarded as idempotent,
    // and class toggles aren't observed, so this settles without looping.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        rescan();
      });
    });
    observer.observe(product, { childList: true, subtree: true });
  }

  function init() {
    initGroupToggleDelegation(document);
    initModalUx();
    initModalItemUx();
    initModalConfirmFix();
    initModalMetaEnhancement();
    document.querySelectorAll('.product').forEach((product) => {
      // Icons must render immediately, including below-the-fold Easify groups.
      initTitleIcons(product);
      initIncludedPsuLabels(product);
      // Warranty Cover restyle hooks + "+ / **" copy formatting (EM-246).
      initWarrantyCover(product);
      // Tag accessories group ASAP so CSS Section 13 scoping kicks in.
      initAccessoriesGroupTag(product);
      // Relocate the "See all" price under the selected product name.
      initPopupButtonPrice(product);
      // GPU-aware monitor filtering must apply above the fold without waiting
      // for the lazy-load scroll trigger.
      initRadioMonitorEnhancement(product);

      if (window.initLazyScript) {
        window.initLazyScript(product, () => initForProduct(product));
      } else {
        initForProduct(product);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('on:bfcache:load-restore', init, { once: true });
})();
